"use client";
import { useCurrentMember } from "@/components/contexts/CurrentMemberContext";
import { ReservationGuideDialog } from "@/components/medical-checkups/ReservationGuideDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  type FacilityDescription,
  type FacilityOption,
  type FacilityPlan,
  type HealthCheckupFacilityContract,
  type MedicalCheckupOption,
  type MedicalCheckupPlan,
  type MedicalCheckupSegment,
  calculateOptionSelfPayAmount,
  calculatePlanSelfPayAmount,
} from "@/server/models/medical-checkup/medical-checkups";
import { pagesPath } from "@/utils/pages-path";
import ExternalLinkIcon from "@public/icons/external-link-icon.svg";
import Link from "next/link";
import { useState } from "react";
import { MdCheck, MdClose, MdExpandLess, MdExpandMore } from "react-icons/md";

export type PlanSubsidy = {
  max_amount_of_subsidy: number;
  business_burden_amount: number;
};

export type OptionSubsidy = {
  max_amount_of_subsidy: number;
};

const ItemDescription = ({
  label,
  description,
  price,
  prices,
  hasSubsidy,
  fiscalYear,
}: {
  label: string;
  description: string;
  price?: number;
  prices?: { plan_code: string; plan_label: string; price: number }[];
  hasSubsidy?: boolean;
  fiscalYear?: number;
}) => (
  <div className="flex min-h-[200px] flex-col rounded-lg border-b border-monotone-2 pb-6">
    <div className="flex-1">
      <div className="mb-3 flex items-center gap-2">
        <h3 className="text-lg font-bold text-primary-6">{label}</h3>
      </div>
      <p className="mb-4 text-sm text-monotone-9">{description}</p>
    </div>
    {prices && (
      <div className="space-y-1 text-right font-semibold text-monotone-8">
        <div className="text-xs">自己負担額</div>
        <div className="text-sm">
          {prices.map((price, index) => (
            <div key={index} className="flex items-baseline justify-between">
              <span className="text-xs font-normal">{price.plan_label}</span>
              <div className="flex items-baseline gap-1 text-base text-primary-6 tabular-nums">
                <span>{price.price.toLocaleString()}円</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}
    {price !== undefined && (
      <div className="space-y-1 text-right font-semibold">
        {hasSubsidy && (
          <span className="rounded-md border border-primary-6 px-1 py-0.5 text-xs font-bold text-primary-6">
            {fiscalYear}年度補助対象
          </span>
        )}
        <div className="text-xs text-monotone-8">自己負担額</div>
        <div className="text-2xl text-primary-6 tabular-nums">
          {price.toLocaleString()}円
        </div>
      </div>
    )}
  </div>
);

export const Facility2025 = ({
  fiscalYear,
  code,
  name,
  address,
  description,
  segment,
  plans,
  options,
  reservationUrl,
  reservationTel,
  url,
  planSubsidies,
  optionSubsidies,
  allOptions,
  mapPlaceId,
  contract,
}: {
  fiscalYear: number;
  code: string;
  name: string;
  address: string | null;
  description: FacilityDescription;
  segment: MedicalCheckupSegment;
  plans: FacilityPlan[];
  options: FacilityOption[];
  reservationUrl: string | null;
  reservationTel: string | null;
  url: string | null;
  planSubsidies: Record<MedicalCheckupPlan, PlanSubsidy>;
  optionSubsidies?: Record<MedicalCheckupOption, OptionSubsidy>;
  allOptions?: Record<MedicalCheckupOption, { segment: MedicalCheckupSegment }>;
  mapPlaceId: string | null;
  contract?: HealthCheckupFacilityContract;
}) => {
  const currentMember = useCurrentMember();
  const [open, setOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const handleReservationGuideClick = () => {
    setOpen(true);
  };

  const reservationFormUrl = pagesPath.members.medical_checkups
    ._fiscalYear(fiscalYear)
    .upsert.$url({
      query: { code },
    }).path;
  const features = [
    {
      name: "外国語対応",
      available: description.multiLanguage,
    },
    {
      name: "バリアフリー対応",
      available: description.barrierFree,
    },
  ];
  // 女性専用・男性専用は segmentの判定がtrueになる時のみ表示する
  if (segment === "female") {
    features.push({ name: "女性専用", available: "Yes" });
  }
  if (segment === "male") {
    features.push({ name: "男性専用", available: "Yes" });
  }

  let query = `&query=${encodeURIComponent(name)}`;
  if (address) {
    query += `,${encodeURIComponent(address)}`;
  }
  let query_place_id = "";
  if (mapPlaceId) {
    query_place_id = `&query_place_id=${mapPlaceId}`;
  }
  const googleMapApiUrl = `https://www.google.com/maps/search/?api=1${query}${query_place_id}`;
  return (
    <>
      <div className="space-y-8 rounded-lg bg-monotone-0.5 px-4 py-6 md:pt-8">
        <div className="flex-col md:flex md:flex-row md:items-start md:justify-between">
          <div className="space-y-4 md:mr-8">
            <div className="space-y-2">
              <h1 className="mb-2 text-xl font-bold">{name}</h1>
              <p className="text-sm">
                <Link
                  href={googleMapApiUrl}
                  className="text-primary-6 underline"
                  target="_blank"
                  prefetch={false}
                >
                  {address}
                  <ExternalLinkIcon className="ml-1 inline h-4 w-4 fill-primary-6 md:h-5 md:w-5" />
                </Link>
              </p>
            </div>
            {contract && contract !== "none" && (
              <div>
                <Badge className="bg-primary-1 px-3 py-1 font-bold text-primary-6 hover:bg-primary-1">
                  {contract === "direct" ? "直契約" : "東振協契約"}
                </Badge>
              </div>
            )}
            <div>
              <p className="mt-2 text-sm">{description.content}</p>
            </div>
          </div>
          <div className="mt-4 flex flex-row gap-3 md:mt-0 md:flex-col">
            <Button
              variant="primary"
              className="flex-1 py-3 md:w-52"
              onClick={() => handleReservationGuideClick()}
            >
              予約案内
            </Button>
            <Button
              variant="secondary"
              className="flex-1 py-3 md:w-52"
              onClick={() => setDetailOpen(!detailOpen)}
            >
              <div className="flex w-full items-center justify-between">
                <span className="flex-1 text-center">
                  {detailOpen ? "閉じる" : "詳細"}
                </span>
                {detailOpen ? (
                  <MdExpandLess className="h-4 w-4" />
                ) : (
                  <MdExpandMore className="h-4 w-4" />
                )}
              </div>
            </Button>
          </div>
        </div>

        <div
          className={cn(
            "space-y-8 transition-all duration-300",
            detailOpen ? "block" : "hidden",
          )}
        >
          <section>
            <div className="mt-4 flex flex-col gap-3 md:flex-row md:flex-wrap">
              {features
                .filter((feature) => feature.available !== "Unknown")
                .map((feature, index) => (
                  <div
                    key={index}
                    className="flex w-full items-center justify-center gap-2 rounded-full bg-monotone-2 px-4 py-2 text-xs font-bold md:w-48"
                  >
                    <div>{feature.name}</div>
                    {feature.available === "Yes" ? (
                      <span className="text-base text-primary-6">
                        有 <MdCheck className="inline h-4 w-4" />
                      </span>
                    ) : (
                      <span className="text-base text-error">
                        無 <MdClose className="inline h-4 w-4" />
                      </span>
                    )}
                  </div>
                ))}
            </div>
          </section>

          <div className="space-y-12 border-t border-monotone-2 pt-8">
            <section>
              <div className="mb-6 items-center gap-2">
                <h2 className="text-lg font-bold">健診コース</h2>
                <span className="text-sm">
                  (補助対象の健診コースが表示されます)
                </span>
              </div>
              <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
                {/* plans はサーバ側で display_order 順に整列済みのため並べ替えは不要 */}
                {plans
                  .filter((plan) => planSubsidies[plan.code] != null)
                  .map((plan, index) => {
                    const price =
                      planSubsidies[plan.code] != null
                        ? calculatePlanSelfPayAmount({
                            businessBurdenAmount:
                              planSubsidies[plan.code].business_burden_amount,
                            subsidyAmount:
                              planSubsidies[plan.code].max_amount_of_subsidy,
                            relationshipToPrimary:
                              currentMember.relationshipToPrimary ?? null,
                            priceRange: { min: plan.price, max: plan.price },
                          }).min
                        : // FIXME: ↑でfilterを掛けているのでこのパスには入らないはず
                          plan.price;
                    // 補助があるかどうかを判定
                    const hasSubsidy =
                      planSubsidies[plan.code] != null &&
                      (planSubsidies[plan.code].max_amount_of_subsidy > 0 ||
                        planSubsidies[plan.code].business_burden_amount > 0);

                    return (
                      <ItemDescription
                        key={index}
                        {...plan}
                        price={price}
                        hasSubsidy={hasSubsidy}
                        fiscalYear={fiscalYear}
                      />
                    );
                  })}
              </div>
            </section>

            <section>
              <div className="mb-6 items-center gap-2">
                <h2 className="text-lg font-bold">オプション検査</h2>
                <span className="text-sm">(受診可能な検査が表示されます)</span>
              </div>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                {/* options はサーバ側で display_order 順に整列済みのため並べ替えは不要 */}
                {options
                  .filter(
                    (option) =>
                      allOptions === undefined ||
                      allOptions[option.code]?.segment === "general" ||
                      allOptions[option.code]?.segment === currentMember.sex,
                  )
                  .map((option, index) => {
                    const price =
                      option.price !== null
                        ? optionSubsidies &&
                          optionSubsidies[option.code] != null
                          ? calculateOptionSelfPayAmount({
                              subsidyAmount:
                                optionSubsidies[option.code]
                                  ?.max_amount_of_subsidy,
                              priceRange: {
                                min: option.price,
                                max: option.price,
                              },
                            }).min
                          : option.price
                        : undefined;
                    const prices =
                      optionSubsidies && optionSubsidies[option.code] != null
                        ? option.prices?.map((price) => ({
                            ...price,
                            price: calculateOptionSelfPayAmount({
                              subsidyAmount:
                                optionSubsidies[option.code]
                                  ?.max_amount_of_subsidy,
                              priceRange: {
                                min: price.price,
                                max: price.price,
                              },
                            }).min,
                          }))
                        : (option.prices ?? undefined);
                    // 補助があるかどうかを判定
                    const hasSubsidy =
                      optionSubsidies &&
                      optionSubsidies[option.code] != null &&
                      optionSubsidies[option.code].max_amount_of_subsidy > 0;

                    return (
                      <ItemDescription
                        key={index}
                        {...option}
                        price={price}
                        prices={prices}
                        hasSubsidy={hasSubsidy}
                        fiscalYear={fiscalYear}
                      />
                    );
                  })}
              </div>
            </section>
          </div>
        </div>
      </div>

      <ReservationGuideDialog
        open={open}
        onOpenChange={setOpen}
        reservationUrl={reservationUrl}
        url={url}
        reservationTel={reservationTel}
        reservationFormUrl={reservationFormUrl}
      />
    </>
  );
};
